// @ts-strict-ignore
import React, { useState } from 'react'
import { View, Keyboard } from 'react-native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { useForm } from 'react-hook-form'
import Spinner from 'APP/Components/Spinner'

// Actions
import PatientActions from 'APP/Redux/PatientRedux'

// Components
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import ProfileHeaderCard from 'APP/Components/ProfileHeaderCard'
import DialogueBranding from 'APP/Components/DialogueBranding'
import Button from 'APP/Converse/Button'

import ControllerInput from 'APP/Components/ControllerFields/ControllerInput'
import ControllerPicker from 'APP/Components/ControllerFields/ControllerPicker'

// Lib
import { KeyLabelArrays, Format } from 'APP/Lib/ProfileDataFormat'

// Styling
import Styles from './style'

// I18n
import I18n from 'APP/I18n'
import { ResponsiveScrollView } from 'APP/Converse/Layout'
import { getValidationRulesForTextField } from 'APP/Lib/FormHelpers'
import Pronouns from 'APP/Components/Pronouns'

const UpdateProfileScreen = () => {
  const { requestRunning } = useSelector((state) => state.patient)
  const { profile } = useSelector((state) => state.patient) || {}
  const { signupProfileData } = useSelector((state) => state.login) || {}
  const dispatch = useDispatch()

  const currentPreferredName: string = profile.preferredName || signupProfileData?.preferredName
  const currentPronouns: string = profile.pronouns || signupProfileData?.pronouns
  const currentLanguage: string =
    profile.preferred_language ||
    (KeyLabelArrays.LANGUAGES.length === 1 && KeyLabelArrays.LANGUAGES[0].key)

  const currentGenderIdentity: string = profile.genderIdentity || signupProfileData?.genderIdentity
  const currentGenderIdentityDescription: string =
    profile.genderIdentityDescription || signupProfileData?.genderIdentityDescription

  const [focusedField, setFocusedField] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, isValid },
  } = useForm({
    mode: 'all',
    defaultValues: {
      preferredName: currentPreferredName,
      pronouns: currentPronouns,
      genderIdentity: currentGenderIdentity,
      genderIdentityDescription: currentGenderIdentityDescription,
      phoneNumber: profile.phoneNumber,
      preferredLanguage: currentLanguage,
    },
  })

  const watchGenderIdentity = watch('genderIdentity')

  const onSubmit = (formData) => {
    if (formData.genderIdentity !== 'prefer_to_self_identify') {
      formData.genderIdentityDescription = null // Ensure we don't send a genderIdentityDescription if the user didn't select prefer_to_self_identify
    }

    dispatch(
      PatientActions.patientProfileUpdateRequest({
        preferredName: formData.preferredName?.trim(),
        pronouns: formData.pronouns?.trim(),
        genderIdentity: formData.genderIdentity,
        genderIdentityDescription: formData.genderIdentityDescription?.trim(),
        phoneNumber: parsePhone(formData.phoneNumber),
        preferred_language: formData.preferredLanguage,
      })
    )
  }

  const renderPreferredName = () => {
    return (
      <ControllerInput
        id="edit-profile-preferred-name"
        name="preferredName"
        testID="UPDATE_PROFILE_SCREEN_PREFERRED_NAME_FIELD"
        control={control}
        label={I18n.t('ProfileScreen.preferredNameOptional')}
        infotip={String(I18n.t('UpdateProfileScreen.preferredNameHint'))}
        onFocus={() => setFocusedField('preferredName')}
        isFocused={focusedField === 'preferredName'}
        rules={getValidationRulesForTextField(I18n.t('ProfileScreen.error.invalidPreferredName'), {
          noUrls: true,
          safeChars: true,
          noNumbers: true,
        })}
      />
    )
  }

  const renderPronouns = () => {
    return (
      <Pronouns
        id="edit-profile-pronouns"
        name="pronouns"
        shouldShowPronounRadio={false}
        onFocus={() => setFocusedField('pronouns')}
        isFocused={focusedField === 'pronouns'}
        control={control}
        label={I18n.t('ProfileScreen.pronounsOptional')}
      />
    )
  }

  const renderGenderIdentity = () => {
    const genderIdentities = KeyLabelArrays.GENDER
    return (
      <ControllerPicker
        name="genderIdentity"
        testID="UPDATE_PROFILE_GENDER_IDENTITY_FIELD"
        control={control}
        label={I18n.t('ProfileScreen.genderIdentity')}
        placeholder={String(I18n.t('ProfileScreen.selectGenderIdentity'))}
        items={genderIdentities}
      />
    )
  }

  const renderPreferToSelfIdentify = () => {
    const showPreferToSelfIdentify = watchGenderIdentity === 'prefer_to_self_identify'

    return (
      showPreferToSelfIdentify && (
        <ControllerInput
          id="edit-profile-gender-identity-description"
          name="genderIdentityDescription"
          testID="UPDATE_PROFILE_SCREEN_PREFER_TO_SELF_IDENTIFY_FIELD"
          control={control}
          rules={{
            required: showPreferToSelfIdentify
              ? I18n.t('ProfileScreen.error.invalidPreferToSelfIdentify')
              : false,
          }}
          label={I18n.t('ProfileScreen.genders.preferToSelfIdentify')}
          onFocus={() => setFocusedField('genderIdentityDescription')}
          isFocused={focusedField === 'genderIdentityDescription'}
        />
      )
    )
  }

  const renderPhoneNumber = () => {
    return (
      <ControllerInput
        id="edit-profile-phone-number"
        name="phoneNumber"
        testID="UPDATE_PROFILE_SCREEN_PHONE_NUMBER_FIELD"
        control={control}
        rules={getValidationRulesForTextField(I18n.t('ProfileScreen.error.invalidNumber'), {
          noUrls: true,
          required: true,
        })}
        label={I18n.t('ProfileScreen.number')}
        infotip={String(I18n.t('ProfileScreen.hint.numberHint'))}
        onFocus={() => setFocusedField('phoneNumber')}
        keyboardType="phone-pad"
        isFocused={focusedField === 'phoneNumber'}
      />
    )
  }

  const renderPreferredLanguage = () => {
    return (
      <ControllerPicker
        name="preferredLanguage"
        rules={{
          required: String(I18n.t('ProfileScreen.error.invalidLanguage')),
          validate: (value) =>
            !!Format.language(value) || String(I18n.t('ProfileScreen.error.invalidLanguage')),
        }}
        control={control}
        label={I18n.t('ProfileScreen.language')}
        placeholder={String(I18n.t('UpdateProfileScreen.communicationPref'))}
        items={KeyLabelArrays.LANGUAGES}
        testID="UPDATE_PROFILE_LANG_INPUT"
        isFocused={focusedField === 'preferredLanguage'}
      />
    )
  }

  const renderProfileSummary = () => {
    const { givenName, preferredName, familyName, email, pronouns } = profile

    return (
      <ProfileHeaderCard
        testID="PROFILE_SUMMARY"
        givenName={givenName}
        preferredName={preferredName}
        familyName={familyName}
        pronouns={pronouns}
        subTitle={email}
      />
    )
  }

  const showCommunicationPreference = KeyLabelArrays.LANGUAGES.length > 1

  return (
    <NoticeBarContainer>
      <Spinner visible={requestRunning} cancelable={false} />
      <ResponsiveScrollView alwaysBounceVertical={false} keyboardShouldPersistTaps="always">
        {renderProfileSummary()}
        <View style={Styles.content}>
          {renderPreferredName()}
          {renderPronouns()}
          {renderGenderIdentity()}
          {renderPreferToSelfIdentify()}
          {renderPhoneNumber()}
          {showCommunicationPreference && renderPreferredLanguage()}

          <Button
            variant="primary"
            title={String(I18n.t('UpdateProfileScreen.save'))}
            widthVariant="full"
            onPress={() => {
              Keyboard.dismiss()
              setFocusedField(null)
              handleSubmit(onSubmit)()
            }}
            analyticsName="Done"
            testID="SAVE_BTN"
            disabled={!isDirty || !isValid}
          />
        </View>
      </ResponsiveScrollView>
      {!focusedField && <DialogueBranding />}
    </NoticeBarContainer>
  )
}

const parsePhone = (phoneNumber: string) => phoneNumber.replace(/\D+/g, '')

export default UpdateProfileScreen
